/**
 * Diaspora Client Onboarding Component
 * Specialized onboarding flow for international clients investing in Zimbabwe
 */

import React, { useState } from 'react';
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Card,
    CardContent,
    Grid,
    Alert,
    AlertTitle,
    Chip,
    Avatar,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    FormControlLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress
} from '@mui/material';
import {
    Public,
    AttachMoney,
    Security,
    Schedule,
    CheckCircle,
    Info,
    Verified,
    Home,
    Phone,
    Email,
    LocationOn,
    Language,
    AccountBalance,
    CalendarToday
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const steps = [
    'Your Location',
    'Project Goals',
    'Budget & Timeline',
    'Communication',
    'Verification'
];

// Comprehensive list of cities and towns in Zimbabwe
const zimbabweCities = [
    { value: 'harare', label: 'Harare', province: 'Harare Province' },
    { value: 'bulawayo', label: 'Bulawayo', province: 'Bulawayo Province' },
    { value: 'chitungwiza', label: 'Chitungwiza', province: 'Harare Province' },
    { value: 'mutare', label: 'Mutare', province: 'Manicaland' },
    { value: 'gweru', label: 'Gweru', province: 'Midlands' },
    { value: 'kwekwe', label: 'Kwekwe', province: 'Midlands' },
    { value: 'kadoma', label: 'Kadoma', province: 'Mashonaland West' },
    { value: 'masvingo', label: 'Masvingo', province: 'Masvingo Province' },
    { value: 'chinhoyi', label: 'Chinhoyi', province: 'Mashonaland West' },
    { value: 'marondera', label: 'Marondera', province: 'Mashonaland East' },
    { value: 'norton', label: 'Norton', province: 'Mashonaland West' },
    { value: 'chegutu', label: 'Chegutu', province: 'Mashonaland West' },
    { value: 'bindura', label: 'Bindura', province: 'Mashonaland Central' },
    { value: 'beitbridge', label: 'Beitbridge', province: 'Matabeleland South' },
    { value: 'redcliff', label: 'Redcliff', province: 'Midlands' },
    { value: 'victoria_falls', label: 'Victoria Falls', province: 'Matabeleland North' },
    { value: 'hwange', label: 'Hwange', province: 'Matabeleland North' },
    { value: 'rusape', label: 'Rusape', province: 'Manicaland' },
    { value: 'chiredzi', label: 'Chiredzi', province: 'Masvingo Province' },
    { value: 'kariba', label: 'Kariba', province: 'Mashonaland West' },
    { value: 'karoi', label: 'Karoi', province: 'Mashonaland West' },
    { value: 'zvishavane', label: 'Zvishavane', province: 'Midlands' },
    { value: 'gokwe', label: 'Gokwe', province: 'Midlands' },
    { value: 'shurugwi', label: 'Shurugwi', province: 'Midlands' },
    { value: 'chipinge', label: 'Chipinge', province: 'Manicaland' },
    { value: 'chivhu', label: 'Chivhu', province: 'Mashonaland East' },
    { value: 'gutu', label: 'Gutu', province: 'Masvingo Province' },
    { value: 'mvuma', label: 'Mvuma', province: 'Midlands' },
    { value: 'gwanda', label: 'Gwanda', province: 'Matabeleland South' },
    { value: 'plumtree', label: 'Plumtree', province: 'Matabeleland South' },
    { value: 'lupane', label: 'Lupane', province: 'Matabeleland North' },
    { value: 'insiza', label: 'Insiza', province: 'Matabeleland South' },
    { value: 'esigodini', label: 'Esigodini', province: 'Matabeleland South' },
    { value: 'shamva', label: 'Shamva', province: 'Mashonaland Central' },
    { value: 'mazowe', label: 'Mazowe', province: 'Mashonaland Central' },
    { value: 'mount_darwin', label: 'Mount Darwin', province: 'Mashonaland Central' },
    { value: 'guruve', label: 'Guruve', province: 'Mashonaland Central' },
    { value: 'centenary', label: 'Centenary', province: 'Mashonaland Central' },
    { value: 'glendale', label: 'Glendale', province: 'Mashonaland Central' },
    { value: 'macheke', label: 'Macheke', province: 'Mashonaland East' },
    { value: 'wedza', label: 'Wedza', province: 'Mashonaland East' },
    { value: 'murewa', label: 'Murewa', province: 'Mashonaland East' },
    { value: 'mutoko', label: 'Mutoko', province: 'Mashonaland East' },
    { value: 'nyanga', label: 'Nyanga', province: 'Manicaland' },
    { value: 'chimanimani', label: 'Chimanimani', province: 'Manicaland' },
    { value: 'penhalonga', label: 'Penhalonga', province: 'Manicaland' },
    { value: 'juliasdale', label: 'Juliasdale', province: 'Manicaland' },
    { value: 'hauna', label: 'Hauna', province: 'Manicaland' },
    { value: 'bikita', label: 'Bikita', province: 'Masvingo Province' },
    { value: 'zaka', label: 'Zaka', province: 'Masvingo Province' },
    { value: 'mwenezi', label: 'Mwenezi', province: 'Masvingo Province' },
    { value: 'rutenga', label: 'Rutenga', province: 'Masvingo Province' },
    { value: 'triangle', label: 'Triangle', province: 'Masvingo Province' },
    { value: 'other', label: 'Other (Please specify in suburb field)', province: 'Any' }
].sort((a, b) => a.label.localeCompare(b.label));

// Comprehensive list of countries with their currencies, timezones, and phone codes
const countries = [
    // Africa
    { code: 'DZ', name: 'Algeria', currency: 'DZD', timezone: 'Africa/Algiers', region: 'Africa', phoneCode: '+213' },
    { code: 'AO', name: 'Angola', currency: 'AOA', timezone: 'Africa/Luanda', region: 'Africa', phoneCode: '+244' },
    { code: 'BJ', name: 'Benin', currency: 'XOF', timezone: 'Africa/Porto-Novo', region: 'Africa', phoneCode: '+229' },
    { code: 'BW', name: 'Botswana', currency: 'BWP', timezone: 'Africa/Gaborone', region: 'Africa', phoneCode: '+267' },
    { code: 'BF', name: 'Burkina Faso', currency: 'XOF', timezone: 'Africa/Ouagadougou', region: 'Africa', phoneCode: '+226' },
    { code: 'BI', name: 'Burundi', currency: 'BIF', timezone: 'Africa/Bujumbura', region: 'Africa', phoneCode: '+257' },
    { code: 'CM', name: 'Cameroon', currency: 'XAF', timezone: 'Africa/Douala', region: 'Africa', phoneCode: '+237' },
    { code: 'CV', name: 'Cape Verde', currency: 'CVE', timezone: 'Atlantic/Cape_Verde', region: 'Africa', phoneCode: '+238' },
    { code: 'CF', name: 'Central African Republic', currency: 'XAF', timezone: 'Africa/Bangui', region: 'Africa', phoneCode: '+236' },
    { code: 'TD', name: 'Chad', currency: 'XAF', timezone: 'Africa/Ndjamena', region: 'Africa', phoneCode: '+235' },
    { code: 'KM', name: 'Comoros', currency: 'KMF', timezone: 'Indian/Comoro', region: 'Africa', phoneCode: '+269' },
    { code: 'CG', name: 'Congo', currency: 'XAF', timezone: 'Africa/Brazzaville', region: 'Africa', phoneCode: '+242' },
    { code: 'CD', name: 'Congo (DRC)', currency: 'CDF', timezone: 'Africa/Kinshasa', region: 'Africa', phoneCode: '+243' },
    { code: 'CI', name: 'Côte d\'Ivoire', currency: 'XOF', timezone: 'Africa/Abidjan', region: 'Africa', phoneCode: '+225' },
    { code: 'DJ', name: 'Djibouti', currency: 'DJF', timezone: 'Africa/Djibouti', region: 'Africa', phoneCode: '+253' },
    { code: 'EG', name: 'Egypt', currency: 'EGP', timezone: 'Africa/Cairo', region: 'Africa', phoneCode: '+20' },
    { code: 'GQ', name: 'Equatorial Guinea', currency: 'XAF', timezone: 'Africa/Malabo', region: 'Africa', phoneCode: '+240' },
    { code: 'ER', name: 'Eritrea', currency: 'ERN', timezone: 'Africa/Asmara', region: 'Africa', phoneCode: '+291' },
    { code: 'ET', name: 'Ethiopia', currency: 'ETB', timezone: 'Africa/Addis_Ababa', region: 'Africa', phoneCode: '+251' },
    { code: 'GA', name: 'Gabon', currency: 'XAF', timezone: 'Africa/Libreville', region: 'Africa', phoneCode: '+241' },
    { code: 'GM', name: 'Gambia', currency: 'GMD', timezone: 'Africa/Banjul', region: 'Africa', phoneCode: '+220' },
    { code: 'GH', name: 'Ghana', currency: 'GHS', timezone: 'Africa/Accra', region: 'Africa', phoneCode: '+233' },
    { code: 'GN', name: 'Guinea', currency: 'GNF', timezone: 'Africa/Conakry', region: 'Africa', phoneCode: '+224' },
    { code: 'GW', name: 'Guinea-Bissau', currency: 'XOF', timezone: 'Africa/Bissau', region: 'Africa', phoneCode: '+245' },
    { code: 'KE', name: 'Kenya', currency: 'KES', timezone: 'Africa/Nairobi', region: 'Africa', phoneCode: '+254' },
    { code: 'LS', name: 'Lesotho', currency: 'LSL', timezone: 'Africa/Maseru', region: 'Africa', phoneCode: '+266' },
    { code: 'LR', name: 'Liberia', currency: 'LRD', timezone: 'Africa/Monrovia', region: 'Africa', phoneCode: '+231' },
    { code: 'LY', name: 'Libya', currency: 'LYD', timezone: 'Africa/Tripoli', region: 'Africa', phoneCode: '+218' },
    { code: 'MG', name: 'Madagascar', currency: 'MGA', timezone: 'Indian/Antananarivo', region: 'Africa', phoneCode: '+261' },
    { code: 'MW', name: 'Malawi', currency: 'MWK', timezone: 'Africa/Blantyre', region: 'Africa', phoneCode: '+265' },
    { code: 'ML', name: 'Mali', currency: 'XOF', timezone: 'Africa/Bamako', region: 'Africa', phoneCode: '+223' },
    { code: 'MR', name: 'Mauritania', currency: 'MRU', timezone: 'Africa/Nouakchott', region: 'Africa', phoneCode: '+222' },
    { code: 'MU', name: 'Mauritius', currency: 'MUR', timezone: 'Indian/Mauritius', region: 'Africa', phoneCode: '+230' },
    { code: 'MA', name: 'Morocco', currency: 'MAD', timezone: 'Africa/Casablanca', region: 'Africa', phoneCode: '+212' },
    { code: 'MZ', name: 'Mozambique', currency: 'MZN', timezone: 'Africa/Maputo', region: 'Africa', phoneCode: '+258' },
    { code: 'NA', name: 'Namibia', currency: 'NAD', timezone: 'Africa/Windhoek', region: 'Africa', phoneCode: '+264' },
    { code: 'NE', name: 'Niger', currency: 'XOF', timezone: 'Africa/Niamey', region: 'Africa', phoneCode: '+227' },
    { code: 'NG', name: 'Nigeria', currency: 'NGN', timezone: 'Africa/Lagos', region: 'Africa', phoneCode: '+234' },
    { code: 'RW', name: 'Rwanda', currency: 'RWF', timezone: 'Africa/Kigali', region: 'Africa', phoneCode: '+250' },
    { code: 'ST', name: 'São Tomé and Príncipe', currency: 'STN', timezone: 'Africa/Sao_Tome', region: 'Africa', phoneCode: '+239' },
    { code: 'SN', name: 'Senegal', currency: 'XOF', timezone: 'Africa/Dakar', region: 'Africa', phoneCode: '+221' },
    { code: 'SC', name: 'Seychelles', currency: 'SCR', timezone: 'Indian/Mahe', region: 'Africa', phoneCode: '+248' },
    { code: 'SL', name: 'Sierra Leone', currency: 'SLL', timezone: 'Africa/Freetown', region: 'Africa', phoneCode: '+232' },
    { code: 'SO', name: 'Somalia', currency: 'SOS', timezone: 'Africa/Mogadishu', region: 'Africa', phoneCode: '+252' },
    { code: 'ZA', name: 'South Africa', currency: 'ZAR', timezone: 'Africa/Johannesburg', region: 'Africa', phoneCode: '+27' },
    { code: 'SS', name: 'South Sudan', currency: 'SSP', timezone: 'Africa/Juba', region: 'Africa', phoneCode: '+211' },
    { code: 'SD', name: 'Sudan', currency: 'SDG', timezone: 'Africa/Khartoum', region: 'Africa', phoneCode: '+249' },
    { code: 'SZ', name: 'Eswatini', currency: 'SZL', timezone: 'Africa/Mbabane', region: 'Africa', phoneCode: '+268' },
    { code: 'TZ', name: 'Tanzania', currency: 'TZS', timezone: 'Africa/Dar_es_Salaam', region: 'Africa', phoneCode: '+255' },
    { code: 'TG', name: 'Togo', currency: 'XOF', timezone: 'Africa/Lome', region: 'Africa', phoneCode: '+228' },
    { code: 'TN', name: 'Tunisia', currency: 'TND', timezone: 'Africa/Tunis', region: 'Africa', phoneCode: '+216' },
    { code: 'UG', name: 'Uganda', currency: 'UGX', timezone: 'Africa/Kampala', region: 'Africa', phoneCode: '+256' },
    { code: 'ZM', name: 'Zambia', currency: 'ZMW', timezone: 'Africa/Lusaka', region: 'Africa', phoneCode: '+260' },
    { code: 'ZW', name: 'Zimbabwe', currency: 'USD', timezone: 'Africa/Harare', region: 'Africa', phoneCode: '+263' },
    
    // Americas
    { code: 'AR', name: 'Argentina', currency: 'ARS', timezone: 'America/Argentina/Buenos_Aires', region: 'Americas', phoneCode: '+54' },
    { code: 'BS', name: 'Bahamas', currency: 'BSD', timezone: 'America/Nassau', region: 'Americas', phoneCode: '+1-242' },
    { code: 'BB', name: 'Barbados', currency: 'BBD', timezone: 'America/Barbados', region: 'Americas', phoneCode: '+1-246' },
    { code: 'BZ', name: 'Belize', currency: 'BZD', timezone: 'America/Belize', region: 'Americas', phoneCode: '+501' },
    { code: 'BO', name: 'Bolivia', currency: 'BOB', timezone: 'America/La_Paz', region: 'Americas', phoneCode: '+591' },
    { code: 'BR', name: 'Brazil', currency: 'BRL', timezone: 'America/Sao_Paulo', region: 'Americas', phoneCode: '+55' },
    { code: 'CA', name: 'Canada', currency: 'CAD', timezone: 'America/Toronto', region: 'Americas', phoneCode: '+1' },
    { code: 'CL', name: 'Chile', currency: 'CLP', timezone: 'America/Santiago', region: 'Americas', phoneCode: '+56' },
    { code: 'CO', name: 'Colombia', currency: 'COP', timezone: 'America/Bogota', region: 'Americas', phoneCode: '+57' },
    { code: 'CR', name: 'Costa Rica', currency: 'CRC', timezone: 'America/Costa_Rica', region: 'Americas', phoneCode: '+506' },
    { code: 'CU', name: 'Cuba', currency: 'CUP', timezone: 'America/Havana', region: 'Americas', phoneCode: '+53' },
    { code: 'DM', name: 'Dominica', currency: 'XCD', timezone: 'America/Dominica', region: 'Americas', phoneCode: '+1-767' },
    { code: 'DO', name: 'Dominican Republic', currency: 'DOP', timezone: 'America/Santo_Domingo', region: 'Americas', phoneCode: '+1-809' },
    { code: 'EC', name: 'Ecuador', currency: 'USD', timezone: 'America/Guayaquil', region: 'Americas', phoneCode: '+593' },
    { code: 'SV', name: 'El Salvador', currency: 'USD', timezone: 'America/El_Salvador', region: 'Americas', phoneCode: '+503' },
    { code: 'GD', name: 'Grenada', currency: 'XCD', timezone: 'America/Grenada', region: 'Americas', phoneCode: '+1-473' },
    { code: 'GT', name: 'Guatemala', currency: 'GTQ', timezone: 'America/Guatemala', region: 'Americas', phoneCode: '+502' },
    { code: 'GY', name: 'Guyana', currency: 'GYD', timezone: 'America/Guyana', region: 'Americas', phoneCode: '+592' },
    { code: 'HT', name: 'Haiti', currency: 'HTG', timezone: 'America/Port-au-Prince', region: 'Americas', phoneCode: '+509' },
    { code: 'HN', name: 'Honduras', currency: 'HNL', timezone: 'America/Tegucigalpa', region: 'Americas', phoneCode: '+504' },
    { code: 'JM', name: 'Jamaica', currency: 'JMD', timezone: 'America/Jamaica', region: 'Americas', phoneCode: '+1-876' },
    { code: 'MX', name: 'Mexico', currency: 'MXN', timezone: 'America/Mexico_City', region: 'Americas', phoneCode: '+52' },
    { code: 'NI', name: 'Nicaragua', currency: 'NIO', timezone: 'America/Managua', region: 'Americas', phoneCode: '+505' },
    { code: 'PA', name: 'Panama', currency: 'PAB', timezone: 'America/Panama', region: 'Americas', phoneCode: '+507' },
    { code: 'PY', name: 'Paraguay', currency: 'PYG', timezone: 'America/Asuncion', region: 'Americas', phoneCode: '+595' },
    { code: 'PE', name: 'Peru', currency: 'PEN', timezone: 'America/Lima', region: 'Americas', phoneCode: '+51' },
    { code: 'KN', name: 'Saint Kitts and Nevis', currency: 'XCD', timezone: 'America/St_Kitts', region: 'Americas', phoneCode: '+1-869' },
    { code: 'LC', name: 'Saint Lucia', currency: 'XCD', timezone: 'America/St_Lucia', region: 'Americas', phoneCode: '+1-758' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines', currency: 'XCD', timezone: 'America/St_Vincent', region: 'Americas', phoneCode: '+1-784' },
    { code: 'SR', name: 'Suriname', currency: 'SRD', timezone: 'America/Paramaribo', region: 'Americas', phoneCode: '+597' },
    { code: 'TT', name: 'Trinidad and Tobago', currency: 'TTD', timezone: 'America/Port_of_Spain', region: 'Americas', phoneCode: '+1-868' },
    { code: 'US', name: 'United States', currency: 'USD', timezone: 'America/New_York', region: 'Americas', phoneCode: '+1' },
    { code: 'UY', name: 'Uruguay', currency: 'UYU', timezone: 'America/Montevideo', region: 'Americas', phoneCode: '+598' },
    { code: 'VE', name: 'Venezuela', currency: 'VES', timezone: 'America/Caracas', region: 'Americas', phoneCode: '+58' },
    
    // Asia
    { code: 'AF', name: 'Afghanistan', currency: 'AFN', timezone: 'Asia/Kabul', region: 'Asia', phoneCode: '+93' },
    { code: 'AM', name: 'Armenia', currency: 'AMD', timezone: 'Asia/Yerevan', region: 'Asia', phoneCode: '+374' },
    { code: 'AZ', name: 'Azerbaijan', currency: 'AZN', timezone: 'Asia/Baku', region: 'Asia', phoneCode: '+994' },
    { code: 'BH', name: 'Bahrain', currency: 'BHD', timezone: 'Asia/Bahrain', region: 'Asia', phoneCode: '+973' },
    { code: 'BD', name: 'Bangladesh', currency: 'BDT', timezone: 'Asia/Dhaka', region: 'Asia', phoneCode: '+880' },
    { code: 'BT', name: 'Bhutan', currency: 'BTN', timezone: 'Asia/Thimphu', region: 'Asia', phoneCode: '+975' },
    { code: 'BN', name: 'Brunei', currency: 'BND', timezone: 'Asia/Brunei', region: 'Asia', phoneCode: '+673' },
    { code: 'KH', name: 'Cambodia', currency: 'KHR', timezone: 'Asia/Phnom_Penh', region: 'Asia', phoneCode: '+855' },
    { code: 'CN', name: 'China', currency: 'CNY', timezone: 'Asia/Shanghai', region: 'Asia', phoneCode: '+86' },
    { code: 'GE', name: 'Georgia', currency: 'GEL', timezone: 'Asia/Tbilisi', region: 'Asia', phoneCode: '+995' },
    { code: 'IN', name: 'India', currency: 'INR', timezone: 'Asia/Kolkata', region: 'Asia', phoneCode: '+91' },
    { code: 'ID', name: 'Indonesia', currency: 'IDR', timezone: 'Asia/Jakarta', region: 'Asia', phoneCode: '+62' },
    { code: 'IR', name: 'Iran', currency: 'IRR', timezone: 'Asia/Tehran', region: 'Asia', phoneCode: '+98' },
    { code: 'IQ', name: 'Iraq', currency: 'IQD', timezone: 'Asia/Baghdad', region: 'Asia', phoneCode: '+964' },
    { code: 'IL', name: 'Israel', currency: 'ILS', timezone: 'Asia/Jerusalem', region: 'Asia', phoneCode: '+972' },
    { code: 'JP', name: 'Japan', currency: 'JPY', timezone: 'Asia/Tokyo', region: 'Asia', phoneCode: '+81' },
    { code: 'JO', name: 'Jordan', currency: 'JOD', timezone: 'Asia/Amman', region: 'Asia', phoneCode: '+962' },
    { code: 'KZ', name: 'Kazakhstan', currency: 'KZT', timezone: 'Asia/Almaty', region: 'Asia', phoneCode: '+7' },
    { code: 'KW', name: 'Kuwait', currency: 'KWD', timezone: 'Asia/Kuwait', region: 'Asia', phoneCode: '+965' },
    { code: 'KG', name: 'Kyrgyzstan', currency: 'KGS', timezone: 'Asia/Bishkek', region: 'Asia', phoneCode: '+996' },
    { code: 'LA', name: 'Laos', currency: 'LAK', timezone: 'Asia/Vientiane', region: 'Asia', phoneCode: '+856' },
    { code: 'LB', name: 'Lebanon', currency: 'LBP', timezone: 'Asia/Beirut', region: 'Asia', phoneCode: '+961' },
    { code: 'MY', name: 'Malaysia', currency: 'MYR', timezone: 'Asia/Kuala_Lumpur', region: 'Asia', phoneCode: '+60' },
    { code: 'MV', name: 'Maldives', currency: 'MVR', timezone: 'Indian/Maldives', region: 'Asia', phoneCode: '+960' },
    { code: 'MN', name: 'Mongolia', currency: 'MNT', timezone: 'Asia/Ulaanbaatar', region: 'Asia', phoneCode: '+976' },
    { code: 'MM', name: 'Myanmar', currency: 'MMK', timezone: 'Asia/Yangon', region: 'Asia', phoneCode: '+95' },
    { code: 'NP', name: 'Nepal', currency: 'NPR', timezone: 'Asia/Kathmandu', region: 'Asia', phoneCode: '+977' },
    { code: 'KP', name: 'North Korea', currency: 'KPW', timezone: 'Asia/Pyongyang', region: 'Asia', phoneCode: '+850' },
    { code: 'OM', name: 'Oman', currency: 'OMR', timezone: 'Asia/Muscat', region: 'Asia', phoneCode: '+968' },
    { code: 'PK', name: 'Pakistan', currency: 'PKR', timezone: 'Asia/Karachi', region: 'Asia', phoneCode: '+92' },
    { code: 'PS', name: 'Palestine', currency: 'ILS', timezone: 'Asia/Gaza', region: 'Asia', phoneCode: '+970' },
    { code: 'PH', name: 'Philippines', currency: 'PHP', timezone: 'Asia/Manila', region: 'Asia', phoneCode: '+63' },
    { code: 'QA', name: 'Qatar', currency: 'QAR', timezone: 'Asia/Qatar', region: 'Asia', phoneCode: '+974' },
    { code: 'SA', name: 'Saudi Arabia', currency: 'SAR', timezone: 'Asia/Riyadh', region: 'Asia', phoneCode: '+966' },
    { code: 'SG', name: 'Singapore', currency: 'SGD', timezone: 'Asia/Singapore', region: 'Asia', phoneCode: '+65' },
    { code: 'KR', name: 'South Korea', currency: 'KRW', timezone: 'Asia/Seoul', region: 'Asia', phoneCode: '+82' },
    { code: 'LK', name: 'Sri Lanka', currency: 'LKR', timezone: 'Asia/Colombo', region: 'Asia', phoneCode: '+94' },
    { code: 'SY', name: 'Syria', currency: 'SYP', timezone: 'Asia/Damascus', region: 'Asia', phoneCode: '+963' },
    { code: 'TW', name: 'Taiwan', currency: 'TWD', timezone: 'Asia/Taipei', region: 'Asia', phoneCode: '+886' },
    { code: 'TJ', name: 'Tajikistan', currency: 'TJS', timezone: 'Asia/Dushanbe', region: 'Asia', phoneCode: '+992' },
    { code: 'TH', name: 'Thailand', currency: 'THB', timezone: 'Asia/Bangkok', region: 'Asia', phoneCode: '+66' },
    { code: 'TL', name: 'Timor-Leste', currency: 'USD', timezone: 'Asia/Dili', region: 'Asia', phoneCode: '+670' },
    { code: 'TR', name: 'Turkey', currency: 'TRY', timezone: 'Europe/Istanbul', region: 'Asia', phoneCode: '+90' },
    { code: 'TM', name: 'Turkmenistan', currency: 'TMT', timezone: 'Asia/Ashgabat', region: 'Asia', phoneCode: '+993' },
    { code: 'AE', name: 'United Arab Emirates', currency: 'AED', timezone: 'Asia/Dubai', region: 'Asia', phoneCode: '+971' },
    { code: 'UZ', name: 'Uzbekistan', currency: 'UZS', timezone: 'Asia/Tashkent', region: 'Asia', phoneCode: '+998' },
    { code: 'VN', name: 'Vietnam', currency: 'VND', timezone: 'Asia/Ho_Chi_Minh', region: 'Asia', phoneCode: '+84' },
    { code: 'YE', name: 'Yemen', currency: 'YER', timezone: 'Asia/Aden', region: 'Asia', phoneCode: '+967' },
    
    // Europe
    { code: 'AL', name: 'Albania', currency: 'ALL', timezone: 'Europe/Tirane', region: 'Europe', phoneCode: '+355' },
    { code: 'AD', name: 'Andorra', currency: 'EUR', timezone: 'Europe/Andorra', region: 'Europe', phoneCode: '+376' },
    { code: 'AT', name: 'Austria', currency: 'EUR', timezone: 'Europe/Vienna', region: 'Europe', phoneCode: '+43' },
    { code: 'BY', name: 'Belarus', currency: 'BYN', timezone: 'Europe/Minsk', region: 'Europe', phoneCode: '+375' },
    { code: 'BE', name: 'Belgium', currency: 'EUR', timezone: 'Europe/Brussels', region: 'Europe', phoneCode: '+32' },
    { code: 'BA', name: 'Bosnia and Herzegovina', currency: 'BAM', timezone: 'Europe/Sarajevo', region: 'Europe', phoneCode: '+387' },
    { code: 'BG', name: 'Bulgaria', currency: 'BGN', timezone: 'Europe/Sofia', region: 'Europe', phoneCode: '+359' },
    { code: 'HR', name: 'Croatia', currency: 'EUR', timezone: 'Europe/Zagreb', region: 'Europe', phoneCode: '+385' },
    { code: 'CY', name: 'Cyprus', currency: 'EUR', timezone: 'Asia/Nicosia', region: 'Europe', phoneCode: '+357' },
    { code: 'CZ', name: 'Czech Republic', currency: 'CZK', timezone: 'Europe/Prague', region: 'Europe', phoneCode: '+420' },
    { code: 'DK', name: 'Denmark', currency: 'DKK', timezone: 'Europe/Copenhagen', region: 'Europe', phoneCode: '+45' },
    { code: 'EE', name: 'Estonia', currency: 'EUR', timezone: 'Europe/Tallinn', region: 'Europe', phoneCode: '+372' },
    { code: 'FI', name: 'Finland', currency: 'EUR', timezone: 'Europe/Helsinki', region: 'Europe', phoneCode: '+358' },
    { code: 'FR', name: 'France', currency: 'EUR', timezone: 'Europe/Paris', region: 'Europe', phoneCode: '+33' },
    { code: 'DE', name: 'Germany', currency: 'EUR', timezone: 'Europe/Berlin', region: 'Europe', phoneCode: '+49' },
    { code: 'GR', name: 'Greece', currency: 'EUR', timezone: 'Europe/Athens', region: 'Europe', phoneCode: '+30' },
    { code: 'HU', name: 'Hungary', currency: 'HUF', timezone: 'Europe/Budapest', region: 'Europe', phoneCode: '+36' },
    { code: 'IS', name: 'Iceland', currency: 'ISK', timezone: 'Atlantic/Reykjavik', region: 'Europe', phoneCode: '+354' },
    { code: 'IE', name: 'Ireland', currency: 'EUR', timezone: 'Europe/Dublin', region: 'Europe', phoneCode: '+353' },
    { code: 'IT', name: 'Italy', currency: 'EUR', timezone: 'Europe/Rome', region: 'Europe', phoneCode: '+39' },
    { code: 'XK', name: 'Kosovo', currency: 'EUR', timezone: 'Europe/Belgrade', region: 'Europe', phoneCode: '+383' },
    { code: 'LV', name: 'Latvia', currency: 'EUR', timezone: 'Europe/Riga', region: 'Europe', phoneCode: '+371' },
    { code: 'LI', name: 'Liechtenstein', currency: 'CHF', timezone: 'Europe/Vaduz', region: 'Europe', phoneCode: '+423' },
    { code: 'LT', name: 'Lithuania', currency: 'EUR', timezone: 'Europe/Vilnius', region: 'Europe', phoneCode: '+370' },
    { code: 'LU', name: 'Luxembourg', currency: 'EUR', timezone: 'Europe/Luxembourg', region: 'Europe', phoneCode: '+352' },
    { code: 'MT', name: 'Malta', currency: 'EUR', timezone: 'Europe/Malta', region: 'Europe', phoneCode: '+356' },
    { code: 'MD', name: 'Moldova', currency: 'MDL', timezone: 'Europe/Chisinau', region: 'Europe', phoneCode: '+373' },
    { code: 'MC', name: 'Monaco', currency: 'EUR', timezone: 'Europe/Monaco', region: 'Europe', phoneCode: '+377' },
    { code: 'ME', name: 'Montenegro', currency: 'EUR', timezone: 'Europe/Podgorica', region: 'Europe', phoneCode: '+382' },
    { code: 'NL', name: 'Netherlands', currency: 'EUR', timezone: 'Europe/Amsterdam', region: 'Europe', phoneCode: '+31' },
    { code: 'MK', name: 'North Macedonia', currency: 'MKD', timezone: 'Europe/Skopje', region: 'Europe', phoneCode: '+389' },
    { code: 'NO', name: 'Norway', currency: 'NOK', timezone: 'Europe/Oslo', region: 'Europe', phoneCode: '+47' },
    { code: 'PL', name: 'Poland', currency: 'PLN', timezone: 'Europe/Warsaw', region: 'Europe', phoneCode: '+48' },
    { code: 'PT', name: 'Portugal', currency: 'EUR', timezone: 'Europe/Lisbon', region: 'Europe', phoneCode: '+351' },
    { code: 'RO', name: 'Romania', currency: 'RON', timezone: 'Europe/Bucharest', region: 'Europe', phoneCode: '+40' },
    { code: 'RU', name: 'Russia', currency: 'RUB', timezone: 'Europe/Moscow', region: 'Europe', phoneCode: '+7' },
    { code: 'SM', name: 'San Marino', currency: 'EUR', timezone: 'Europe/San_Marino', region: 'Europe', phoneCode: '+378' },
    { code: 'RS', name: 'Serbia', currency: 'RSD', timezone: 'Europe/Belgrade', region: 'Europe', phoneCode: '+381' },
    { code: 'SK', name: 'Slovakia', currency: 'EUR', timezone: 'Europe/Bratislava', region: 'Europe', phoneCode: '+421' },
    { code: 'SI', name: 'Slovenia', currency: 'EUR', timezone: 'Europe/Ljubljana', region: 'Europe', phoneCode: '+386' },
    { code: 'ES', name: 'Spain', currency: 'EUR', timezone: 'Europe/Madrid', region: 'Europe', phoneCode: '+34' },
    { code: 'SE', name: 'Sweden', currency: 'SEK', timezone: 'Europe/Stockholm', region: 'Europe', phoneCode: '+46' },
    { code: 'CH', name: 'Switzerland', currency: 'CHF', timezone: 'Europe/Zurich', region: 'Europe', phoneCode: '+41' },
    { code: 'UA', name: 'Ukraine', currency: 'UAH', timezone: 'Europe/Kiev', region: 'Europe', phoneCode: '+380' },
    { code: 'GB', name: 'United Kingdom', currency: 'GBP', timezone: 'Europe/London', region: 'Europe', phoneCode: '+44' },
    { code: 'VA', name: 'Vatican City', currency: 'EUR', timezone: 'Europe/Vatican', region: 'Europe', phoneCode: '+379' },
    
    // Oceania
    { code: 'AU', name: 'Australia', currency: 'AUD', timezone: 'Australia/Sydney', region: 'Oceania', phoneCode: '+61' },
    { code: 'FJ', name: 'Fiji', currency: 'FJD', timezone: 'Pacific/Fiji', region: 'Oceania', phoneCode: '+679' },
    { code: 'KI', name: 'Kiribati', currency: 'AUD', timezone: 'Pacific/Tarawa', region: 'Oceania', phoneCode: '+686' },
    { code: 'MH', name: 'Marshall Islands', currency: 'USD', timezone: 'Pacific/Majuro', region: 'Oceania', phoneCode: '+692' },
    { code: 'FM', name: 'Micronesia', currency: 'USD', timezone: 'Pacific/Pohnpei', region: 'Oceania', phoneCode: '+691' },
    { code: 'NR', name: 'Nauru', currency: 'AUD', timezone: 'Pacific/Nauru', region: 'Oceania', phoneCode: '+674' },
    { code: 'NZ', name: 'New Zealand', currency: 'NZD', timezone: 'Pacific/Auckland', region: 'Oceania', phoneCode: '+64' },
    { code: 'PW', name: 'Palau', currency: 'USD', timezone: 'Pacific/Palau', region: 'Oceania', phoneCode: '+680' },
    { code: 'PG', name: 'Papua New Guinea', currency: 'PGK', timezone: 'Pacific/Port_Moresby', region: 'Oceania', phoneCode: '+675' },
    { code: 'WS', name: 'Samoa', currency: 'WST', timezone: 'Pacific/Apia', region: 'Oceania', phoneCode: '+685' },
    { code: 'SB', name: 'Solomon Islands', currency: 'SBD', timezone: 'Pacific/Guadalcanal', region: 'Oceania', phoneCode: '+677' },
    { code: 'TO', name: 'Tonga', currency: 'TOP', timezone: 'Pacific/Tongatapu', region: 'Oceania', phoneCode: '+676' },
    { code: 'TV', name: 'Tuvalu', currency: 'AUD', timezone: 'Pacific/Funafuti', region: 'Oceania', phoneCode: '+688' },
    { code: 'VU', name: 'Vanuatu', currency: 'VUV', timezone: 'Pacific/Efate', region: 'Oceania', phoneCode: '+678' }
].sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by country name

const DiasporaOnboarding = ({ onComplete }) => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        // Location Information
        diaspora_location: '',
        city: '',
        country_code: '',
        phone_country_code: '+263',
        timezone: 'Africa/Harare',
        currency_preference: 'USD',
        
        // Project Goals
        project_type: '',
        project_location_preference: '',
        project_suburb_area: '',
        timeline_expectation: '',
        
        // Budget
        budget_range_min: '',
        budget_range_max: '',
        funding_source: '',
        
        // Communication
        phone_number: '',
        preferred_contact_method: 'email',
        communication_frequency: 'weekly',
        language_preference: 'en',
        has_local_representative: false,
        local_representative_name: '',
        local_representative_phone: '',
        
        // Preferences
        whatsapp_notifications: false,
        sms_notifications: false,
        email_notifications: true,
        video_call_preference: false,
        
        // Visit Planning
        planning_to_visit: false,
        visit_frequency: '',
        first_visit_date: ''
    });

    const [errors, setErrors] = useState({});

    // Handle next step
    const handleNext = () => {
        if (validateStep(activeStep)) {
            if (activeStep === steps.length - 1) {
                handleComplete();
            } else {
                setActiveStep((prevStep) => prevStep + 1);
            }
        }
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    // Validate current step
    const validateStep = (step) => {
        const newErrors = {};
        
        switch (step) {
            case 0: // Location
                if (!formData.diaspora_location) newErrors.diaspora_location = 'Required';
                if (!formData.country_code) newErrors.country_code = 'Required';
                if (!formData.city || !formData.city.trim()) newErrors.city = 'Please enter your city';
                break;
            case 1: // Goals
                if (!formData.project_type) newErrors.project_type = 'Required';
                if (!formData.project_location_preference) newErrors.project_location_preference = 'Required';
                break;
            case 2: // Budget
                if (!formData.budget_range_min) newErrors.budget_range_min = 'Required';
                if (!formData.budget_range_max) newErrors.budget_range_max = 'Required';
                break;
            case 3: // Communication
                if (!formData.phone_number) newErrors.phone_number = 'Required';
                break;
            default:
                break;
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form completion
    const handleComplete = async () => {
        setLoading(true);
        try {
            // Save onboarding data to user profile
            if (updateUser) {
                updateUser(formData);
            }
            
            // Call onComplete callback if provided
            if (onComplete) {
                onComplete(formData);
            }
            
            // TODO: Send onboarding data to backend API
            // await apiClient.post('/users/profile/onboarding', formData);
            
            // Navigate to dashboard (user is already logged in)
            navigate('/dashboard');
        } catch (error) {
            console.error('Error completing onboarding:', error);
            alert('There was an error saving your profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle country selection
    const handleCountryChange = (countryCode) => {
        const country = countries.find(c => c.code === countryCode);
        if (country) {
            setFormData({
                ...formData,
                country_code: countryCode,
                diaspora_location: country.name,
                currency_preference: country.currency,
                timezone: country.timezone,
                phone_country_code: country.phoneCode || '+1'
            });
        }
    };

    // Render step content
    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box>
                        <Alert severity="info" icon={<Public />} sx={{ mb: 3 }}>
                            <AlertTitle>Welcome, Diaspora Investor!</AlertTitle>
                            We've designed Eqaya specifically for investors like you who are building in Zimbabwe from abroad.
                        </Alert>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControl fullWidth error={!!errors.country_code}>
                                    <InputLabel>Current Country of Residence *</InputLabel>
                                    <Select
                                        value={formData.country_code}
                                        onChange={(e) => handleCountryChange(e.target.value)}
                                        label="Current Country of Residence *"
                                    >
                                        {countries.map(country => (
                                            <MenuItem key={country.code} value={country.code}>
                                                {country.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="City *"
                                    value={formData.city}
                                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                                    error={!!errors.city}
                                    helperText={errors.city || "Enter your city name (e.g., London, New York, Johannesburg)"}
                                    placeholder="Type your city name"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Your Timezone"
                                    value={formData.timezone}
                                    disabled
                                    helperText="Automatically set based on your country"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Preferred Currency"
                                    value={formData.currency_preference}
                                    disabled
                                    helperText="For displaying project costs"
                                />
                            </Grid>
                        </Grid>

                        <Paper sx={{ p: 2, mt: 3, bgcolor: 'success.light' }}>
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircle fontSize="small" />
                                We'll automatically adjust all times and dates to your timezone
                            </Typography>
                        </Paper>
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            What are you looking to build?
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Tell us about your construction goals in Zimbabwe
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControl fullWidth error={!!errors.project_type}>
                                    <InputLabel>Project Type *</InputLabel>
                                    <Select
                                        value={formData.project_type}
                                        onChange={(e) => setFormData({...formData, project_type: e.target.value})}
                                        label="Project Type *"
                                    >
                                        <MenuItem value="residential_house">🏠 Residential House</MenuItem>
                                        <MenuItem value="apartment_building">🏢 Apartment Building</MenuItem>
                                        <MenuItem value="commercial">🏪 Commercial Property</MenuItem>
                                        <MenuItem value="renovation">🔨 Renovation/Extension</MenuItem>
                                        <MenuItem value="land_development">🏗️ Land Development</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth error={!!errors.project_location_preference}>
                                    <InputLabel>City/Town in Zimbabwe *</InputLabel>
                                    <Select
                                        value={formData.project_location_preference}
                                        onChange={(e) => setFormData({...formData, project_location_preference: e.target.value})}
                                        label="City/Town in Zimbabwe *"
                                    >
                                        <MenuItem value="" disabled>
                                            <em>Select a city or town</em>
                                        </MenuItem>
                                        {zimbabweCities.map(city => (
                                            <MenuItem key={city.value} value={city.value}>
                                                <Box>
                                                    <Typography variant="body1">{city.label}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {city.province}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.project_location_preference && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                                            {errors.project_location_preference}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Suburb/Area/Location Name"
                                    value={formData.project_suburb_area}
                                    onChange={(e) => setFormData({...formData, project_suburb_area: e.target.value})}
                                    placeholder="e.g., Glen Norah, Borrowdale, Chivi, Mt Pleasant, Luveve"
                                    helperText="Specify the suburb, neighborhood, or specific area where you want to build"
                                    InputProps={{
                                        startAdornment: <LocationOn sx={{ mr: 1, color: 'action.active' }} />
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Timeline Expectation</InputLabel>
                                    <Select
                                        value={formData.timeline_expectation}
                                        onChange={(e) => setFormData({...formData, timeline_expectation: e.target.value})}
                                        label="Timeline Expectation"
                                    >
                                        <MenuItem value="immediate">Start Immediately (Ready in 2-4 weeks)</MenuItem>
                                        <MenuItem value="1-3months">1-3 Months</MenuItem>
                                        <MenuItem value="3-6months">3-6 Months</MenuItem>
                                        <MenuItem value="6-12months">6-12 Months</MenuItem>
                                        <MenuItem value="flexible">Flexible</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                );

            case 2:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Budget & Funding
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Help us match you with the right contractors
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={`Minimum Budget (${formData.currency_preference}) *`}
                                    value={formData.budget_range_min}
                                    onChange={(e) => setFormData({...formData, budget_range_min: e.target.value})}
                                    error={!!errors.budget_range_min}
                                    InputProps={{
                                        startAdornment: <AttachMoney />
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={`Maximum Budget (${formData.currency_preference}) *`}
                                    value={formData.budget_range_max}
                                    onChange={(e) => setFormData({...formData, budget_range_max: e.target.value})}
                                    error={!!errors.budget_range_max}
                                    InputProps={{
                                        startAdornment: <AttachMoney />
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Funding Source</InputLabel>
                                    <Select
                                        value={formData.funding_source}
                                        onChange={(e) => setFormData({...formData, funding_source: e.target.value})}
                                        label="Funding Source"
                                    >
                                        <MenuItem value="savings">Personal Savings</MenuItem>
                                        <MenuItem value="mortgage">Mortgage/Loan</MenuItem>
                                        <MenuItem value="investment">Investment Fund</MenuItem>
                                        <MenuItem value="business">Business Revenue</MenuItem>
                                        <MenuItem value="inheritance">Inheritance/Gift</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Alert severity="info" icon={<Security />} sx={{ mt: 3 }}>
                            <AlertTitle>Your Investment is Protected</AlertTitle>
                            All funds are held in your secure wallet until work is verified. You have full control over milestone payments.
                        </Alert>
                    </Box>
                );

            case 3:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Communication Preferences
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            How would you like us to keep you updated?
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                        label="Code"
                                        value={formData.phone_country_code}
                                        disabled
                                        sx={{ width: '100px' }}
                                        InputProps={{
                                            startAdornment: <Phone sx={{ mr: 0.5, color: 'action.active' }} />
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Phone Number *"
                                        value={formData.phone_number}
                                        onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                                        error={!!errors.phone_number}
                                        helperText={errors.phone_number || `Enter phone without country code (e.g., ${formData.phone_country_code === '+263' ? '771234567' : formData.phone_country_code === '+44' ? '7700900000' : formData.phone_country_code === '+1' ? '2025551234' : '123456789'})`}
                                        placeholder={formData.phone_country_code === '+263' ? '771234567' : formData.phone_country_code === '+44' ? '7700900000' : formData.phone_country_code === '+1' ? '2025551234' : 'Your phone number'}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Preferred Contact Method</InputLabel>
                                    <Select
                                        value={formData.preferred_contact_method}
                                        onChange={(e) => setFormData({...formData, preferred_contact_method: e.target.value})}
                                        label="Preferred Contact Method"
                                    >
                                        <MenuItem value="email">📧 Email</MenuItem>
                                        <MenuItem value="whatsapp">💬 WhatsApp</MenuItem>
                                        <MenuItem value="phone">📞 Phone Call</MenuItem>
                                        <MenuItem value="sms">📱 SMS</MenuItem>
                                        <MenuItem value="video">🎥 Video Call</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Update Frequency</InputLabel>
                                    <Select
                                        value={formData.communication_frequency}
                                        onChange={(e) => setFormData({...formData, communication_frequency: e.target.value})}
                                        label="Update Frequency"
                                    >
                                        <MenuItem value="daily">Daily Updates</MenuItem>
                                        <MenuItem value="weekly">Weekly Summaries</MenuItem>
                                        <MenuItem value="biweekly">Bi-weekly Reports</MenuItem>
                                        <MenuItem value="milestones">Milestone Updates Only</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Notification Channels
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.email_notifications}
                                                onChange={(e) => setFormData({...formData, email_notifications: e.target.checked})}
                                            />
                                        }
                                        label="Email Notifications"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.whatsapp_notifications}
                                                onChange={(e) => setFormData({...formData, whatsapp_notifications: e.target.checked})}
                                            />
                                        }
                                        label="WhatsApp Notifications (Recommended for diaspora)"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.sms_notifications}
                                                onChange={(e) => setFormData({...formData, sms_notifications: e.target.checked})}
                                            />
                                        }
                                        label="SMS Notifications"
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.has_local_representative}
                                            onChange={(e) => setFormData({...formData, has_local_representative: e.target.checked})}
                                        />
                                    }
                                    label="I have a local representative in Zimbabwe"
                                />
                            </Grid>

                            {formData.has_local_representative && (
                                <>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Local Representative Name"
                                            value={formData.local_representative_name}
                                            onChange={(e) => setFormData({...formData, local_representative_name: e.target.value})}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Representative Phone Number"
                                            value={formData.local_representative_phone}
                                            onChange={(e) => setFormData({...formData, local_representative_phone: e.target.value})}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Box>
                );

            case 4:
                return (
                    <Box>
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'success.main' }}>
                                <CheckCircle sx={{ fontSize: 40 }} />
                            </Avatar>
                            <Typography variant="h5" gutterBottom>
                                You're Almost Ready!
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Review your information and complete your profile
                            </Typography>
                        </Box>

                        <Paper sx={{ p: 3, mb: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Your Location
                            </Typography>
                            <Typography variant="body2">
                                {formData.diaspora_location}, {formData.city}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Timezone: {formData.timezone} • Currency: {formData.currency_preference}
                            </Typography>
                        </Paper>

                        <Paper sx={{ p: 3, mb: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Project Goals
                            </Typography>
                            <Typography variant="body2">
                                {formData.project_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Location: {zimbabweCities.find(c => c.value === formData.project_location_preference)?.label || formData.project_location_preference}
                                {formData.project_suburb_area && ` - ${formData.project_suburb_area}`}
                            </Typography>
                        </Paper>

                        <Paper sx={{ p: 3, mb: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Budget Range
                            </Typography>
                            <Typography variant="body2">
                                {formData.currency_preference} {Number(formData.budget_range_min).toLocaleString()} - {Number(formData.budget_range_max).toLocaleString()}
                            </Typography>
                        </Paper>

                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Communication
                            </Typography>
                            <Typography variant="body2">
                                {formData.phone_number}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Preferred: {formData.preferred_contact_method} • Updates: {formData.communication_frequency}
                            </Typography>
                        </Paper>

                        <Alert severity="success" icon={<Security />}>
                            <AlertTitle>What Happens Next?</AlertTitle>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircle fontSize="small" color="success" /></ListItemIcon>
                                    <ListItemText primary="Your profile will be created" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle fontSize="small" color="success" /></ListItemIcon>
                                    <ListItemText primary="We'll match you with verified contractors" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle fontSize="small" color="success" /></ListItemIcon>
                                    <ListItemText primary="You can start creating your first project" />
                                </ListItem>
                            </List>
                        </Alert>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                    Welcome to Eqaya
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Let's set up your profile for the best experience
                </Typography>
            </Box>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Content */}
            <Card elevation={3}>
                <CardContent sx={{ p: 4 }}>
                    {renderStepContent(activeStep)}
                </CardContent>
            </Card>

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button
                    disabled={activeStep === 0 || loading}
                    onClick={handleBack}
                    size="large"
                >
                    Back
                </Button>
                <Box>
                    {activeStep === steps.length - 1 ? (
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleComplete}
                            disabled={loading}
                            sx={{ minWidth: 200 }}
                        >
                            {loading ? <LinearProgress sx={{ width: '100%' }} /> : 'Complete Setup'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleNext}
                            sx={{ minWidth: 150 }}
                        >
                            Next
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default DiasporaOnboarding;

